import React, {FC, useCallback, useContext} from 'react';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {PushContext} from '../../../../../srcCommon/push/pushContext';
import {buildTeamCounterKey} from '../../../../helpers/counterHelpers';
import {buildTeamPath} from '../../../../helpers/modelHelpers';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {InboxModel} from '../../../../models/api/inboxModel';
import {CountersVisibilityModesEnum} from '../../../../models/api/preferencesModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {InboxesFiltersEnum, TeamsFoldersEnum} from '../../../../models/url/inboxesUrlModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../../services/tracking';
import {reorderInboxInTeamWorkspaceAsyncAction} from '../../../../store/data/workspace/workspaceActions';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {canDropWorkspaceItemAtTarget} from '../dnd/workspaceDragDropHelpers';
import {WorkspaceDragSourceTypesEnum, WorkspaceItemDropHandler} from '../dnd/workspaceDragDropTypes';
import {WorkspaceTeamItemContainer} from '../workspaceTeamItemContainer';
import {TeamWorkspaceInboxItem} from './teamWorkspaceInboxItem';

/*
 * Props.
 */

interface TeamWorkspaceInboxesProps {
  panelId: InboxesPanelId;
  team: TeamModel;
  selectedFilter: InboxesFiltersEnum;
  inboxes: ReadonlyArray<InboxModel>;
  countersVisibility: CountersVisibilityModesEnum;
  isExpanded: boolean;
  /** Handler to expand/collapse. If undefined, the chevron will be hidden and the content will not be expanded. */
  onToggleExpandClick?: Handler;
  /** Whether to show a demo tip explaining what the team workspace is. */
  shouldShowDemoInboxTip?: boolean;
  shouldAlphabetizeWorkspace: boolean;
  hasEditPermission: boolean;
}

/*
 * Component.
 */

export const TeamWorkspaceInboxes: FC<TeamWorkspaceInboxesProps> = (props) => {
  const {
    panelId,
    team,
    countersVisibility,
    isExpanded,
    onToggleExpandClick,
    selectedFilter,
    shouldShowDemoInboxTip,
    shouldAlphabetizeWorkspace,
  } = props;
  const {isConnected} = useContext(PushContext);
  const isDisabled = !isConnected;

  const dispatch = useAppDispatch();
  const onWorkspaceItemDrop: WorkspaceItemDropHandler = useCallback(
    (droppedItem, linePosition, target) => {
      if (!target || !canDropWorkspaceItemAtTarget(droppedItem, target)) {
        return;
      }

      if (
        droppedItem.type !== WorkspaceDragSourceTypesEnum.TEAM_INBOX ||
        target.type !== WorkspaceDragSourceTypesEnum.TEAM_INBOX
      ) {
        return;
      }

      trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_REORDER, {
        section: WorkspaceDisplaySectionTrackingTypesEnum.TEAM_INBOX,
      });
      dispatch(
        reorderInboxInTeamWorkspaceAsyncAction({
          teamId: team.id,
          droppedItemId: droppedItem.id,
          targetItemId: target.id,
          linePosition,
        }),
      );
    },
    [dispatch, team.id],
  );

  return (
    <>
      {/* Aggregate view. */}
      <WorkspaceTeamItemContainer
        panelId={panelId}
        team={team}
        isExpanded={isExpanded}
        onToggleExpandClick={onToggleExpandClick}
        to={buildTeamPath(team.id, TeamsFoldersEnum.INBOX, selectedFilter)}
        counterKey={buildTeamCounterKey(team.id, countersVisibility)}
        countersVisibility={countersVisibility}
        isDisabled={isDisabled}
        shouldShowDemoInboxTip={shouldShowDemoInboxTip}
        shouldAlphabetizeWorkspace={shouldAlphabetizeWorkspace}
        shouldHideMoreMenu={false}
      />

      {/* Inboxes. */}
      {maybeRenderExpandedContent(props, isDisabled, onWorkspaceItemDrop)}
    </>
  );
};

function maybeRenderExpandedContent(
  props: TeamWorkspaceInboxesProps,
  isDisabled: boolean,
  onWorkspaceItemDrop: WorkspaceItemDropHandler,
) {
  const {
    panelId,
    isExpanded,
    inboxes,
    countersVisibility,
    onToggleExpandClick,
    shouldAlphabetizeWorkspace,
    hasEditPermission,
  } = props;
  if (!isExpanded || !onToggleExpandClick) {
    return null;
  }

  return (
    <>
      {/* Inboxes. */}
      {inboxes.map((inbox, index) => (
        <TeamWorkspaceInboxItem
          shouldAlphabetizeWorkspace={shouldAlphabetizeWorkspace}
          key={inbox.id}
          panelId={panelId}
          inbox={inbox}
          countersVisibility={countersVisibility}
          isDisabled={isDisabled}
          onWorkspaceItemDrop={onWorkspaceItemDrop}
          isLastItem={index === inboxes.length - 1}
          hasEditPermission={hasEditPermission}
        />
      ))}
    </>
  );
}
